import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { getNonRKPayInvoiceStatusLabel } from "@constants";
import { Modal } from "../../../../../components";
import "./ConnectedInvoicesModal.scss";

const ConnectedInvoicesModal = ({ show, invoices, onHide, router }) => {
	const { lang } = global;

	const onCloseModal = () => {
		onHide();
	};

	const handleInvoiceNavigation = (invoiceId) => {
		onCloseModal();
		// When using ConnectedInvoicesModal from JobDetails page, the classname "moda-open" is not removed from the body
		// causing the page to be unscrollable. Adding a slight delay before navigating to ensure the modal is fully closed.
		setTimeout(() => {
			router.push(`/payments/manualinvoice?id=${invoiceId}`);
		}, 50);
	};

	const getInvoiceId = (invoice) => invoice.InvoiceId || invoice.NonRKPayInvoiceId;

	const getOfficerDisplayName = (invoice) => invoice.Items && invoice.Items.length === 1
			? invoice.Items[0].OfficerName || lang.t("Unknown")
			: lang.t("Multiple Officers");

	const getPositionDisplayName = (invoice) => invoice.Items && invoice.Items.length === 1
			? invoice.Items[0].JobTitle || lang.t("Unknown")
			: lang.t("Multiple Positions");

	const sortedInvoices = useMemo(() => {
		if (!invoices || invoices.length === 0) return [];

		return [...invoices].sort((a, b) => {
			const aOfficerName = getOfficerDisplayName(a);
			const bOfficerName = getOfficerDisplayName(b);
			return aOfficerName.localeCompare(bOfficerName);
		});
	}, [invoices]);

	return (
		<Modal
			id="connected-invoices-modal"
			size="xl"
			show={show}
			onHide={() => onCloseModal()}
		>
			<Modal.Header closeButton>
				<Modal.Title>{lang.t("Connected Invoice")} </Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="connected-invoices-table">
					<div className="connected-invoices-header hidden-xs">
						<div className="row">
							<strong className="col-sm-3">{lang.t("Officer Name")}</strong>
							<strong className="col-sm-3">{lang.t("Position Name")}</strong>
							<strong className="col-sm-3">{lang.t("Invoice ID")}</strong>
							<strong className="col-sm-3">{lang.t("Invoice Status")}</strong>
						</div>
					</div>
					<div className="connected-invoices-body">
						{sortedInvoices.length > 0 ? (
							sortedInvoices.map(invoice => (
								<div
									className="connected-invoices-item"
									key={getInvoiceId(invoice)}
								>
									<div className="row">
										<div className="col-xs-12 col-sm-3">
											{getOfficerDisplayName(invoice)}
										</div>
										<div className="col-xs-12 col-sm-3">
											{getPositionDisplayName(invoice)}
										</div>
										<div className="col-xs-12 col-sm-3">
											<button
												type="button"
												onClick={() =>
													handleInvoiceNavigation(getInvoiceId(invoice))
												}
												className="btn btn-link padding-left-0"
											>
												{invoice.InvoiceNumber}
											</button>
										</div>
										<div className="col-xs-12 col-sm-3">
											{getNonRKPayInvoiceStatusLabel(invoice.StatusId)}
										</div>
									</div>
								</div>
							))
						) : (
							<div className="col-xs-12 text-center text-muted display-empty">
								{lang.t("No invoices to display")}
							</div>
						)}
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					className="btn btn-primary"
					id="close-connected-invoices-modal"
					onClick={() => onCloseModal()}
				>
					{lang.t("Close")}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

ConnectedInvoicesModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	invoices: PropTypes.array,
	router: PropTypes.object.isRequired,
};

ConnectedInvoicesModal.defaultProps = {
	show: false,
	onHide: () => {},
	invoices: [],
};

export default ConnectedInvoicesModal;
